/**
 * A window during which a normally-paid feature is free for everyone in the BeamXR Live app, or a
 * switch-style feature is on. The server attaches no meaning to featureKey; each app build decides
 * which keys it honours and ignores the rest.
 */
export interface FeaturePromotion {
  /** Absent until created. */
  id?: string | null;
  featureKey: string;
  /** ISO 8601, UTC. Inclusive. */
  startsAtUtc: string;
  /** ISO 8601, UTC. Exclusive. */
  endsAtUtc: string;
  isEnabled: boolean;
  /** Admin note; never sent to the apps. */
  description?: string | null;
  createdUtc?: string | null;
  updatedUtc?: string | null;
}

export type FeaturePromotionStatus = "live" | "scheduled" | "ended" | "disabled";

/**
 * Parses a UTC timestamp from the API. A C# DateTime whose kind was lost serialises without an
 * offset; everything this API sends is UTC, so a bare timestamp is read as UTC rather than as the
 * browser's local time.
 */
export function parseUtc(value: string): Date {
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  return new Date(hasZone ? value : `${value}Z`);
}

/** Where a promotion stands right now, the same way the apps will judge it. */
export function featurePromotionStatus(
  promotion: FeaturePromotion,
  now: Date = new Date()
): FeaturePromotionStatus {
  if (!promotion.isEnabled) return "disabled";
  const starts = parseUtc(promotion.startsAtUtc);
  const ends = parseUtc(promotion.endsAtUtc);
  if (now >= ends) return "ended";
  if (now < starts) return "scheduled";
  return "live";
}
